import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Grid } from "@mui/material";
import { useDispatch } from "react-redux";

import FormikRadioGroupField from "../../components/shared/form/FormikRadioGroupField";
import FormikRadioGroup from "../../components/shared/form/FormikRadioGroup";
import { FormikLabeledTextField } from "../../components/shared/form/FormikLabeledTextField";
import FormikCheckbox from "../../components/shared/form/FormikCheckbox";
import MisdeliveryF18NoteInlineForm from "../../components/misdelivery/MisdeliveryF18NoteInlineForm";
import Loader from "../../components/loader/loader";
import {
  S_GRID_PROPS,
  XL_GRID_PROPS,
  YES_NO_OPTIONS,
  MAX_TEXT_LENGTH,
  L_GRID_PROPS,
  M_GRID_PROPS,
} from "../../constants";
import { ErrorMessages } from "../../constants/constantsMsg";
import {
  useMisdeliveryAuditForm,
  MisdeliveryAuditFormValues,
} from "../../@hooks/useMisdeliveryAuditForm";
import { MisDeliveryStatus } from "../../models/osd";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { CreateMSDNotes } from "../../API/CreateMSDNotes";

type MisdeliveryAuditProps = {
  osdNumber?: number;
  proNumberOptions?: Array<number | string>;
  initialData?: Partial<MisdeliveryAuditFormValues>;
  investigationStatus?: MisDeliveryStatus;
  f18NoteData?: { proNumbers: number[]; note: string };
  onSave?: (values: MisdeliveryAuditFormValues) => Promise<void> | void;
  onComplete?: (values: MisdeliveryAuditFormValues) => Promise<void> | void;
  onRefresh?: () => void;
};

const MisdeliveryAudit: React.FC<MisdeliveryAuditProps> = ({
  osdNumber,
  proNumberOptions,
  initialData,
  investigationStatus,
  f18NoteData,
  onSave,
  onComplete,
  onRefresh,
}) => {
  const [showAddF18NoteForm, setShowAddF18NoteForm] = useState(false);
  const [isEditingF18Note, setIsEditingF18Note] = useState(false);
  const [msdPro, setMsdPro] = useState<number[]>(f18NoteData?.proNumbers ?? []);
  const [msdNote, setMsdNote] = useState(f18NoteData?.note ?? "");
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  // Check if F18 note exists
  const hasExistingF18Note = Boolean(
    f18NoteData?.note?.trim() || (f18NoteData?.proNumbers?.length ?? 0) > 0,
  );

  // Sync local state when f18NoteData changes
  useEffect(() => {
    setMsdPro(f18NoteData?.proNumbers ?? []);
    setMsdNote(f18NoteData?.note ?? "");
  }, [f18NoteData]);

  // Form is editable when investigationStatus is investigationComplete, or in edit mode for other statuses
  const isInvestigationComplete =
    investigationStatus === MisDeliveryStatus.investigationComplete;
  const isNeedCleared = investigationStatus === MisDeliveryStatus.needsCleared;
  const [isEditMode, setIsEditMode] = useState(isInvestigationComplete);

  const f18ProOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (proNumberOptions ?? [])
            .map((option) => Number(option))
            .filter(Number.isFinite),
        ),
      ),
    [proNumberOptions],
  );

  const { formik, handleSave, handleComplete, handleCancel } =
    useMisdeliveryAuditForm({
      osdNumber,
      initialData,
      onSave: async (values) => {
        await onSave?.(values);
        setIsEditMode(false);
      },
      onComplete: async (values) => {
        await onComplete?.(values);
        setIsEditMode(false);
      },
    });

  const {
    values,
    touched,
    errors,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formik;
  // Form is disabled when not in edit mode OR when status is needCleared
  const isDisabled = !isEditMode || isNeedCleared;

  // Track if complete was attempted (to show validation errors)
  const [completeAttempted, setCompleteAttempted] = useState(false);

  // Helper to get checkbox error - show error if touched OR complete was attempted
  const getCheckboxError = useCallback(
    (name: keyof MisdeliveryAuditFormValues): string | undefined => {
      const hasError = (touched[name] || completeAttempted) && errors[name];
      return hasError ? String(errors[name]) : undefined;
    },
    [touched, errors, completeAttempted],
  );

  // Helper to create checkbox onChange handler
  const createCheckboxHandler = useCallback(
    (name: keyof MisdeliveryAuditFormValues, validate = true) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(name, event.target.checked ? "Y" : "", validate);
      },
    [setFieldValue],
  );

  const onEditClick = useCallback(() => setIsEditMode(true), []);
  const onResetClick = useCallback(() => {
    handleCancel(); // Reset form to initial values only
    setCompleteAttempted(false);
  }, [handleCancel]);

  // Helper to scroll to first error field
  const scrollToFirstError = useCallback((errorKeys: string[]) => {
    if (errorKeys.length > 0) {
      const firstErrorField = errorKeys[0];
      document?.getElementsByName(firstErrorField)?.[0]?.focus();
    }
  }, []);

  const onSaveClick = useCallback(async () => {
    // Validate only didSaiaRecoverMisdelvFreight field for save
    if (!values.didSaiaRecoverMisdelvFreight) {
      setFieldTouched("didSaiaRecoverMisdelvFreight", true, true);
      await formik.validateField("didSaiaRecoverMisdelvFreight");
      document?.getElementsByName("didSaiaRecoverMisdelvFreight")?.[0]?.focus();
      return;
    }

    setIsLoading(true);
    try {
      await handleSave();
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.SUCCESS,
          ErrorMessages.misdeliveryAuditSaved,
          new Date().toISOString(),
        ),
      );
      onRefresh?.();
    } catch (error: unknown) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string" ? error : "Failed to save audit",
          new Date().toISOString(),
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    handleSave,
    dispatch,
    onRefresh,
    values.didSaiaRecoverMisdelvFreight,
    setFieldTouched,
    formik,
  ]);

  const onCompleteClick = useCallback(async () => {
    setCompleteAttempted(true);

    // Validate form and scroll to first error
    const validationErrors = await formik.validateForm();
    const errorKeys = Object.keys(validationErrors);
    if (errorKeys.length > 0) {
      // Touch all fields with errors to show validation messages
      errorKeys.forEach((key) => {
        setFieldTouched(key, true, false);
      });
      scrollToFirstError(errorKeys);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const success = await handleComplete();
      if (success) {
        setCompleteAttempted(false);
        dispatch(
          PopoutNotification(
            NOTIFICATION_TYPE.SUCCESS,
            ErrorMessages.misdeliveryAuditCompleted,
            new Date().toISOString(),
          ),
        );
        onRefresh?.();
      }
    } catch (error: unknown) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string" ? error : "Failed to complete audit",
          new Date().toISOString(),
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    handleComplete,
    dispatch,
    onRefresh,
    formik,
    scrollToFirstError,
    setFieldTouched,
  ]);

  const onSubmitF18Note = useCallback(async () => {
    if (!osdNumber || msdPro.length === 0 || !msdNote.trim()) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          "Please select PRO number(s) and enter a note",
          new Date().toISOString(),
        ),
      );
      return;
    }

    setIsLoading(true);
    try {
      await CreateMSDNotes({
        exceptionNumber: osdNumber,
        proNumbers: msdPro,
        note: msdNote,
        section: "audit",
      });
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.SUCCESS,
          ErrorMessages.msdNotesCreatedSuccess,
          new Date().toISOString(),
        ),
      );
      setMsdPro([]);
      setMsdNote("");
      setShowAddF18NoteForm(false);
      setIsEditingF18Note(false);
      onRefresh?.();
    } catch (error: unknown) {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string" ? error : "Failed to create MSD Notes",
          new Date().toISOString(),
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [osdNumber, msdPro, msdNote, dispatch, onRefresh]);

  // BOL selection handler
  const handleBolChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedValue = event.target.value;
      const isSaia = selectedValue === "SAIA";

      // Set BOL values without triggering validation
      setFieldValue(
        "recoveredFreightSaiaMisdelvBol",
        isSaia ? "Y" : "N",
        false,
      );
      setFieldValue(
        "recoveredFreightCustomerProvidedBol",
        isSaia ? "N" : "Y",
        false,
      );

      // Reset touched state for all BOL-related checkbox fields to prevent premature validation
      // Clear touched for SAIA BOL fields
      setFieldTouched("saiaMisdelvBolConfirmBilling", false, false);
      setFieldTouched("saiaMisdelvBolConfirmImaged", false, false);
      // Clear touched for Customer BOL fields
      setFieldTouched("customerProvidedBolConfirmBilling", false, false);
      setFieldTouched("customerProvidedBolConfirmImaged", false, false);

      // Mark the BOL selection itself as touched
      setFieldTouched("recoveredFreightSaiaMisdelvBol", true, false);
    },
    [setFieldValue, setFieldTouched],
  );

  // PRO type selection handler
  const handleProTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isOriginal = event.target.value === "ORIGINAL";

      // Set PRO type values without triggering validation
      setFieldValue(
        "misdelvFreightMoveOriginalPro",
        isOriginal ? "Y" : "N",
        false,
      );
      setFieldValue(
        "misdelvFreightMoveSuffixPro",
        isOriginal ? "N" : "Y",
        false,
      );

      // Reset touched state for related fields to prevent premature validation
      setFieldTouched("misdelvFreightMoveOriginalPro", false, false);
      setFieldTouched("misdelvFreightMoveSuffixPro", false, false);
    },
    [setFieldValue, setFieldTouched],
  );

  const isRecoveredY = values.didSaiaRecoverMisdelvFreight === "Y";
  const isRecoveredN = values.didSaiaRecoverMisdelvFreight === "N";
  const isSaiaBol = values.recoveredFreightSaiaMisdelvBol === "Y";
  const isCustomerBol = values.recoveredFreightCustomerProvidedBol === "Y";
  const isOriginalPro = values.misdelvFreightMoveOriginalPro === "Y";
  const isSuffixPro = values.misdelvFreightMoveSuffixPro === "Y";

  return (
    <Box>
      <Loader pageLoader={isLoading} />
      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          className="filter-margin"
        >
          <h3 className="accordion-content-header">
            Misdelv Audit - Procedure
          </h3>
        </Grid>

        <Grid container spacing={2} className="filter-margin">
          <FormikRadioGroupField
            formik={formik}
            name="didSaiaRecoverMisdelvFreight"
            label="Did Saia recover the MISDELV freight"
            options={YES_NO_OPTIONS}
            required
            disabled={isDisabled}
            gridProps={S_GRID_PROPS}
          />

          {isRecoveredN && (
            <>
              <FormikLabeledTextField
                formik={formik}
                name="didSaiaRecoverMisdelvFreightWhy"
                label="Why ?"
                placeholder="Enter reason"
                required
                disabled={isDisabled}
                maxLength={MAX_TEXT_LENGTH}
                gridProps={M_GRID_PROPS}
                multiline={true}
                rows={2}
              />
              <Grid item md={0.5} xs={12} />
              <FormikCheckbox
                name="notRecoveredFreightStatementOrDrImaged"
                label="Statement or DR imaged"
                required
                disabled={isDisabled}
                error={getCheckboxError(
                  "notRecoveredFreightStatementOrDrImaged",
                )}
                checked={values.notRecoveredFreightStatementOrDrImaged === "Y"}
                onChange={createCheckboxHandler(
                  "notRecoveredFreightStatementOrDrImaged",
                )}
                onBlur={handleBlur}
                gridProps={{
                  md: 2.5,
                  xs: 12,
                  sx: { display: "flex", alignItems: "center", paddingTop: 1 },
                }}
              />
            </>
          )}

          {isRecoveredY && (
            <>
              <FormikLabeledTextField
                formik={formik}
                name="recoveredFreightNewProNumber"
                label="New PRO #"
                placeholder="Enter new PRO #"
                type="number"
                required
                disabled={isDisabled}
                maxLength={12}
                gridProps={S_GRID_PROPS}
              />

              <FormikRadioGroup
                name="selectBol"
                label="Select BOL"
                options={[
                  { value: "SAIA", label: "SAIA MISDELV BOL" },
                  { value: "CUSTOMER", label: "Customer provided BOL" },
                ]}
                required
                disabled={isDisabled}
                row
                value={isSaiaBol ? "SAIA" : isCustomerBol ? "CUSTOMER" : ""}
                onChange={handleBolChange}
                onBlur={() =>
                  setFieldTouched("recoveredFreightSaiaMisdelvBol", true, true)
                }
                error={
                  touched.recoveredFreightSaiaMisdelvBol &&
                  errors.recoveredFreightSaiaMisdelvBol
                    ? String(errors.recoveredFreightSaiaMisdelvBol)
                    : undefined
                }
                gridProps={{ xs: 12, md: 6 }}
              />
            </>
          )}
        </Grid>
        {/* SAIA MISDELV BOL Fields */}
        {isSaiaBol && (
          <>
            <Grid container spacing={2} className="filter-margin">
              <FormikCheckbox
                name="saiaMisdelvBolConfirmBilling"
                label="Confirm billing"
                required
                disabled={isDisabled}
                error={getCheckboxError("saiaMisdelvBolConfirmBilling")}
                checked={values.saiaMisdelvBolConfirmBilling === "Y"}
                onChange={createCheckboxHandler("saiaMisdelvBolConfirmBilling")}
                onBlur={handleBlur}
                gridProps={S_GRID_PROPS}
              />
              {values.saiaMisdelvBolConfirmBilling === "Y" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="saiaMisdelvBolConfirmBillingDetail"
                  label="Additional details"
                  placeholder="Enter additional details"
                  required
                  disabled={isDisabled}
                  maxLength={MAX_TEXT_LENGTH}
                  gridProps={L_GRID_PROPS}
                  multiline={true}
                  rows={2}
                />
              )}
            </Grid>
            <Grid container spacing={2} className="filter-margin">
              <FormikCheckbox
                name="saiaMisdelvBolConfirmImaged"
                label="Confirmed imaged"
                required
                disabled={isDisabled}
                error={getCheckboxError("saiaMisdelvBolConfirmImaged")}
                checked={values.saiaMisdelvBolConfirmImaged === "Y"}
                onChange={createCheckboxHandler("saiaMisdelvBolConfirmImaged")}
                onBlur={handleBlur}
                gridProps={S_GRID_PROPS}
              />
              {values.saiaMisdelvBolConfirmImaged === "Y" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="saiaMisdelvBolConfirmImagedDetail"
                  label="Additional details"
                  placeholder="Enter additional details"
                  required
                  disabled={isDisabled}
                  maxLength={MAX_TEXT_LENGTH}
                  gridProps={L_GRID_PROPS}
                  multiline={true}
                  rows={2}
                />
              )}
            </Grid>
          </>
        )}

        {/* Customer Provided BOL Fields */}
        {isCustomerBol && (
          <>
            <Grid container spacing={2} className="filter-margin">
              <FormikCheckbox
                name="customerProvidedBolConfirmBilling"
                label="Confirm billing"
                required
                disabled={isDisabled}
                error={getCheckboxError("customerProvidedBolConfirmBilling")}
                checked={values.customerProvidedBolConfirmBilling === "Y"}
                onChange={createCheckboxHandler(
                  "customerProvidedBolConfirmBilling",
                )}
                onBlur={handleBlur}
                gridProps={S_GRID_PROPS}
              />
              {values.customerProvidedBolConfirmBilling === "Y" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="customerProvidedBolConfirmBillingDetail"
                  label="Additional details"
                  placeholder="Enter additional details"
                  required
                  disabled={isDisabled}
                  maxLength={MAX_TEXT_LENGTH}
                  gridProps={L_GRID_PROPS}
                  multiline={true}
                  rows={2}
                />
              )}
            </Grid>
            <Grid container spacing={2} className="filter-margin">
              <FormikCheckbox
                name="customerProvidedBolConfirmImaged"
                label="Confirmed imaged"
                required
                disabled={isDisabled}
                error={getCheckboxError("customerProvidedBolConfirmImaged")}
                checked={values.customerProvidedBolConfirmImaged === "Y"}
                onChange={createCheckboxHandler(
                  "customerProvidedBolConfirmImaged",
                )}
                onBlur={handleBlur}
                gridProps={S_GRID_PROPS}
              />
              {values.customerProvidedBolConfirmImaged === "Y" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="customerProvidedBolConfirmImagedDetail"
                  label="Additional details"
                  placeholder="Enter additional details"
                  required
                  disabled={isDisabled}
                  maxLength={MAX_TEXT_LENGTH}
                  gridProps={L_GRID_PROPS}
                  multiline={true}
                  rows={2}
                />
              )}
            </Grid>
          </>
        )}
        {/* <Grid container spacing={2} className="filter-margin"> */}
        {isRecoveredY && (
          <>
            <Grid container spacing={2} className="filter-margin">
              <FormikRadioGroupField
                formik={formik}
                name="recoveredFreightNewProAllShort"
                label="Is the NEW PRO all short ?"
                options={YES_NO_OPTIONS}
                required
                disabled={isDisabled}
                gridProps={S_GRID_PROPS}
              />
            </Grid>
            <Grid container spacing={2} className="filter-margin">
              <FormikCheckbox
                name="recoveredFreightNewProShowsProOrNbOv"
                label="86 for NEW PRO shows PRO or NB/OV"
                required
                disabled={isDisabled}
                error={getCheckboxError("recoveredFreightNewProShowsProOrNbOv")}
                checked={values.recoveredFreightNewProShowsProOrNbOv === "Y"}
                onChange={createCheckboxHandler(
                  "recoveredFreightNewProShowsProOrNbOv",
                )}
                onBlur={handleBlur}
                gridProps={S_GRID_PROPS}
              />

              {values.recoveredFreightNewProShowsProOrNbOv === "Y" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="newProShowsProOrNbOvDetail"
                  label="Additional details"
                  placeholder="Enter additional details"
                  required
                  disabled={isDisabled}
                  maxLength={MAX_TEXT_LENGTH}
                  multiline={true}
                  rows={2}
                  gridProps={L_GRID_PROPS}
                />
              )}
            </Grid>
            <Grid container spacing={2} className="filter-margin">
              <FormikCheckbox
                name="recoveredFreightConfirmDrImaged"
                label="Confirm DR imaged"
                required
                disabled={isDisabled}
                error={getCheckboxError("recoveredFreightConfirmDrImaged")}
                checked={values.recoveredFreightConfirmDrImaged === "Y"}
                onChange={createCheckboxHandler(
                  "recoveredFreightConfirmDrImaged",
                )}
                onBlur={handleBlur}
                gridProps={S_GRID_PROPS}
              />

              {values.recoveredFreightConfirmDrImaged === "Y" && (
                <>
                  <FormikRadioGroupField
                    formik={formik}
                    name="drImagedSignedShowingAppliesToProOrNbOv"
                    label="Signed showing applies to PRO or NB/OV"
                    options={YES_NO_OPTIONS}
                    required
                    disabled={isDisabled}
                    gridProps={S_GRID_PROPS}
                  />
                  {values.drImagedSignedShowingAppliesToProOrNbOv === "Y" && (
                    <FormikLabeledTextField
                      formik={formik}
                      name="signedAppliesToProOrNbOvExceptionNumber"
                      label="Exception #"
                      placeholder="Enter exception"
                      required
                      disabled={isDisabled}
                      maxLength={11}
                      gridProps={S_GRID_PROPS}
                    />
                  )}
                </>
              )}
            </Grid>
          </>
        )}
        {/* </Grid> */}

        {values.didSaiaRecoverMisdelvFreight !== "N" && (
          <Grid container spacing={2} className="filter-margin">
            <FormikRadioGroupField
              formik={formik}
              name="didMisdelvPickupLeaveConsigneeShort"
              label="Did the MISDELV P/U leave the consignee Short"
              options={YES_NO_OPTIONS}
              required
              disabled={isDisabled}
              gridProps={S_GRID_PROPS}
            />

            {values.didMisdelvPickupLeaveConsigneeShort === "N" && (
              <FormikLabeledTextField
                formik={formik}
                name="misdelvPickupConsigneeShortAdditionalComments"
                label="Additional details"
                placeholder="Enter additional details"
                required
                disabled={isDisabled}
                maxLength={MAX_TEXT_LENGTH}
                gridProps={L_GRID_PROPS}
                multiline={true}
                rows={2}
              />
            )}

            {values.didMisdelvPickupLeaveConsigneeShort === "Y" && (
              <>
                <FormikRadioGroupField
                  formik={formik}
                  name="didRepresentativeUpdateDeliveryStatus"
                  label="Did the representative update the delivery status after MSD P/U"
                  options={YES_NO_OPTIONS}
                  required
                  disabled={isDisabled}
                  gridProps={S_GRID_PROPS}
                />

                {values.didRepresentativeUpdateDeliveryStatus === "N" && (
                  <FormikLabeledTextField
                    formik={formik}
                    name="representativeDeliveryStatusAdditionalComments"
                    label="Additional details"
                    placeholder="Enter additional details"
                    required
                    disabled={isDisabled}
                    maxLength={MAX_TEXT_LENGTH}
                    gridProps={M_GRID_PROPS}
                    multiline={true}
                    rows={2}
                  />
                )}
              </>
            )}
            <Grid
              container
              spacing={4}
              style={{ marginLeft: -15, marginTop: -5 }}
            >
              <FormikRadioGroupField
                formik={formik}
                name="didMisdelvFreightMoveCorrectly"
                label="Did the MISDELV freight move correctly on the Original PRO or Suffix PRO"
                options={YES_NO_OPTIONS}
                required
                disabled={isDisabled}
                gridProps={S_GRID_PROPS}
              />

              {values.didMisdelvFreightMoveCorrectly === "Y" && (
                <>
                  <FormikRadioGroup
                    name="selectProType"
                    label="Select Which one"
                    options={[
                      { value: "ORIGINAL", label: "Original PRO" },
                      { value: "SUFFIX", label: "Suffix PRO" },
                    ]}
                    required
                    disabled={isDisabled}
                    row
                    value={
                      isOriginalPro ? "ORIGINAL" : isSuffixPro ? "SUFFIX" : ""
                    }
                    onChange={handleProTypeChange}
                    onBlur={() =>
                      setFieldTouched(
                        "misdelvFreightMoveOriginalPro",
                        true,
                        true,
                      )
                    }
                    error={
                      touched.misdelvFreightMoveOriginalPro &&
                      errors.misdelvFreightMoveOriginalPro
                        ? String(errors.misdelvFreightMoveOriginalPro)
                        : undefined
                    }
                    gridProps={S_GRID_PROPS}
                  />
                  <FormikRadioGroupField
                    formik={formik}
                    name="correctConsigneeReceivedCompleteOrder"
                    label="Has the correct consignee for the apply to PRO now receive their complete order ?"
                    options={YES_NO_OPTIONS}
                    required
                    disabled={isDisabled}
                    gridProps={S_GRID_PROPS}
                  />

                  {values.correctConsigneeReceivedCompleteOrder === "N" && (
                    <FormikRadioGroupField
                      formik={formik}
                      name="correctConsigneeOrderDoesDeliveryStatusShowOpenShortage"
                      label="Does the delv status still show an open shortage"
                      options={YES_NO_OPTIONS}
                      required
                      disabled={isDisabled}
                      gridProps={S_GRID_PROPS}
                    />
                  )}
                </>
              )}

              {values.didMisdelvFreightMoveCorrectly === "N" && (
                <FormikLabeledTextField
                  formik={formik}
                  name="misdelvFreightMoveCorrectlyAdditionalComments"
                  label="Additional details"
                  placeholder="Enter additional details"
                  required
                  disabled={isDisabled}
                  maxLength={MAX_TEXT_LENGTH}
                  gridProps={L_GRID_PROPS}
                  multiline={true}
                  rows={2}
                />
              )}
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              marginTop={0}
              paddingTop={0}
              className="filter-padding"
            >
              <Grid container direction="column" rowSpacing={1}>
                <FormikCheckbox
                  name="recoveredFreightConfirmOption14"
                  label="Confirm Option 14"
                  required
                  disabled={isDisabled}
                  error={getCheckboxError("recoveredFreightConfirmOption14")}
                  checked={values.recoveredFreightConfirmOption14 === "Y"}
                  onChange={createCheckboxHandler(
                    "recoveredFreightConfirmOption14",
                  )}
                  onBlur={handleBlur}
                  gridProps={S_GRID_PROPS}
                />

                <FormikCheckbox
                  name="recoveredFreightConfirmF18NotesClosingStatement"
                  label="Confirm F18 notes show closing statement"
                  required
                  disabled={isDisabled}
                  error={getCheckboxError(
                    "recoveredFreightConfirmF18NotesClosingStatement",
                  )}
                  checked={
                    values.recoveredFreightConfirmF18NotesClosingStatement ===
                    "Y"
                  }
                  onChange={createCheckboxHandler(
                    "recoveredFreightConfirmF18NotesClosingStatement",
                  )}
                  onBlur={handleBlur}
                  gridProps={S_GRID_PROPS}
                />

                <FormikCheckbox
                  name="recoveredFreightMsdExceptionAppliesToPro"
                  label="78/6 the MSD exception showing applied to PRO"
                  required
                  disabled={isDisabled}
                  error={getCheckboxError(
                    "recoveredFreightMsdExceptionAppliesToPro",
                  )}
                  checked={
                    values.recoveredFreightMsdExceptionAppliesToPro === "Y"
                  }
                  onChange={createCheckboxHandler(
                    "recoveredFreightMsdExceptionAppliesToPro",
                  )}
                  onBlur={handleBlur}
                  gridProps={S_GRID_PROPS}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Action Buttons - Always visible based on edit mode and investigation status */}
        <div className="btn-group-submit">
          {/* Hide all buttons when needCleared */}
          {!isNeedCleared && (
            <>
              {isEditMode && (
                <>
                  {/* Show Clear label for investigationComplete, Cancel for other statuses */}
                  <Button
                    type="button"
                    variant="contained"
                    className="clear-btn mr-0 ml"
                    onClick={onResetClick}
                  >
                    {isInvestigationComplete ? "Clear" : "Cancel"}
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    className="entry mr-0 ml"
                    onClick={onSaveClick}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    className="entry mr-0 ml"
                    onClick={onCompleteClick}
                  >
                    Complete
                  </Button>
                </>
              )}
              {!isEditMode && (
                <>
                  {!showAddF18NoteForm &&
                    !isEditingF18Note &&
                    !hasExistingF18Note && (
                      <Button
                        type="button"
                        variant="contained"
                        className="assign mr-0 ml"
                        onClick={() => setShowAddF18NoteForm(true)}
                      >
                        Add F18 Note
                      </Button>
                    )}
                  {!showAddF18NoteForm && !isEditingF18Note && (
                    <Button
                      type="button"
                      variant="contained"
                      className="assign mr-0 ml"
                      onClick={onEditClick}
                    >
                      Edit
                    </Button>
                  )}
                </>
              )}
              {/* Show F18 note buttons also when in edit mode for investigationComplete status */}
              {isEditMode &&
                isInvestigationComplete &&
                !showAddF18NoteForm &&
                !isEditingF18Note &&
                !hasExistingF18Note && (
                  <Button
                    type="button"
                    variant="contained"
                    className="assign mr-0 ml"
                    onClick={() => setShowAddF18NoteForm(true)}
                  >
                    Add F18 Note
                  </Button>
                )}
            </>
          )}
        </div>

        {/* Hide F18 Note form when needCleared */}
        {!isNeedCleared && (
          <Grid container spacing={2} className="filter-margin">
            {/* Show existing F18 Note as read-only */}
            {hasExistingF18Note && !isEditingF18Note && !showAddF18NoteForm && (
              <Grid item {...XL_GRID_PROPS}>
                <MisdeliveryF18NoteInlineForm
                  msdPro={f18NoteData?.proNumbers ?? []}
                  proOptions={f18ProOptions}
                  note={f18NoteData?.note ?? ""}
                  onMsdProChange={setMsdPro}
                  onNoteChange={setMsdNote}
                  onEdit={() => setIsEditingF18Note(true)}
                  readOnly
                />
              </Grid>
            )}
            {/* Show editable F18 Note form */}
            {(showAddF18NoteForm || isEditingF18Note) && (
              <Grid item {...XL_GRID_PROPS}>
                <MisdeliveryF18NoteInlineForm
                  msdPro={msdPro}
                  proOptions={f18ProOptions}
                  note={msdNote}
                  onMsdProChange={setMsdPro}
                  onNoteChange={setMsdNote}
                  onCancelF18Note={() => {
                    setMsdPro(f18NoteData?.proNumbers ?? []);
                    setMsdNote(f18NoteData?.note ?? "");
                    setShowAddF18NoteForm(false);
                    setIsEditingF18Note(false);
                  }}
                  onSubmitF18Note={onSubmitF18Note}
                />
              </Grid>
            )}
          </Grid>
        )}
      </form>
    </Box>
  );
};

export default MisdeliveryAudit;
